(() => {
  const select = (selector, root = document) => root.querySelector(selector);
  const selectAll = (selector, root = document) => [...root.querySelectorAll(selector)];
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const navToggle = select('[data-nav-toggle]');
  const mobileMenu = select('[data-mobile-menu]');
  const navLabel = select('[data-nav-label]');

  const closeNavigation = () => {
    if (!navToggle || !mobileMenu) return;
    navToggle.setAttribute('aria-expanded', 'false');
    if (navLabel) navLabel.textContent = 'Open menu';
    mobileMenu.hidden = true;
    document.body.classList.remove('nav-open');
  };

  if (navToggle && mobileMenu) {
    navToggle.addEventListener('click', () => {
      const willOpen = navToggle.getAttribute('aria-expanded') !== 'true';
      navToggle.setAttribute('aria-expanded', String(willOpen));
      if (navLabel) navLabel.textContent = willOpen ? 'Close menu' : 'Open menu';
      mobileMenu.hidden = !willOpen;
      document.body.classList.toggle('nav-open', willOpen);
      if (willOpen) select('a', mobileMenu)?.focus();
    });
    selectAll('a', mobileMenu).forEach((link) => link.addEventListener('click', closeNavigation));
    window.addEventListener('resize', () => { if (window.innerWidth >= 990) closeNavigation(); });
  }

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && document.body.classList.contains('nav-open')) {
      closeNavigation();
      navToggle?.focus();
    }
  });

  const video = select('[data-campaign-video]');
  const videoToggle = select('[data-video-toggle]');
  const videoLabel = select('[data-video-label]');
  const pauseIcon = select('[data-pause-icon]');
  const playIcon = select('[data-play-icon]');

  const updateVideoControl = () => {
    if (!video || !videoToggle) return;
    const paused = video.paused;
    videoToggle.setAttribute('aria-pressed', String(paused));
    if (videoLabel) videoLabel.textContent = paused ? 'Play film' : 'Pause film';
    if (pauseIcon) pauseIcon.hidden = paused;
    if (playIcon) playIcon.hidden = !paused;
  };

  if (video && videoToggle) {
    if (reduceMotion) video.pause();
    video.addEventListener('play', updateVideoControl);
    video.addEventListener('pause', updateVideoControl);
    videoToggle.addEventListener('click', async () => {
      if (video.paused) {
        try { await video.play(); } catch (_) { /* The control remains in a truthful paused state. */ }
      } else {
        video.pause();
      }
      updateVideoControl();
    });
    updateVideoControl();
  }

  const dialog = select('[data-waitlist-dialog]');
  const dialogLook = select('[data-waitlist-look]');
  const dialogTags = select('[data-waitlist-tags]');
  const waitlistForm = select('#WaitlistForm');
  const emailInput = select('#WaitlistEmail');
  const emailError = select('[data-email-error]');
  const consentInput = select('input[name="contact[accepts_marketing]"]', waitlistForm);
  const consentError = select('[data-consent-error]');
  let waitlistTrigger = null;

  const restoreWaitlistFocus = () => {
    if (waitlistTrigger instanceof HTMLElement && document.contains(waitlistTrigger)) waitlistTrigger.focus();
    waitlistTrigger = null;
  };

  const openWaitlist = (trigger) => {
    if (!dialog) return;
    const priorityTag = trigger?.dataset.waitlistTag;
    if (!['waitlist-edore', 'waitlist-efe'].includes(priorityTag)) return;
    waitlistTrigger = trigger;
    const look = trigger?.dataset.look || 'your chosen look';
    if (dialogLook) dialogLook.textContent = look;
    if (dialogTags) {
      dialogTags.value = `Ishokare priority, ${priorityTag}`;
      dialogTags.setAttribute('value', dialogTags.value);
    }
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    window.setTimeout(() => emailInput?.focus(), 40);
  };

  const closeWaitlist = () => {
    if (!dialog) return;
    if (typeof dialog.close === 'function') dialog.close();
    else {
      dialog.removeAttribute('open');
      restoreWaitlistFocus();
    }
  };

  selectAll('[data-waitlist-open]').forEach((button) => button.addEventListener('click', () => openWaitlist(button)));
  selectAll('[data-waitlist-close]').forEach((button) => button.addEventListener('click', closeWaitlist));
  dialog?.addEventListener('click', (event) => { if (event.target === dialog) closeWaitlist(); });
  dialog?.addEventListener('close', restoreWaitlistFocus);
  if (select('[data-form-success]', dialog)) {
    if (typeof dialog?.showModal === 'function') dialog.showModal();
    else dialog?.setAttribute('open', '');
  }

  waitlistForm?.addEventListener('submit', (event) => {
    if (!emailInput) return;
    let firstInvalid = null;
    const emailMessage = !emailInput.value.trim()
      ? 'Enter your email address.'
      : (!emailInput.validity.valid ? 'Enter a valid email address.' : '');
    if (emailError) emailError.textContent = emailMessage;
    emailInput.toggleAttribute('aria-invalid', Boolean(emailMessage));
    if (emailMessage) firstInvalid = emailInput;

    const consentMessage = consentInput && !consentInput.checked
      ? 'Please confirm that you agree to receive Ishokare emails.'
      : '';
    if (consentError) consentError.textContent = consentMessage;
    consentInput?.toggleAttribute('aria-invalid', Boolean(consentMessage));
    if (!firstInvalid && consentMessage) firstInvalid = consentInput;

    if (firstInvalid) {
      event.preventDefault();
      firstInvalid.focus();
    }
  });
  emailInput?.addEventListener('input', () => {
    emailInput.removeAttribute('aria-invalid');
    if (emailError) emailError.textContent = '';
  });
  consentInput?.addEventListener('change', () => {
    consentInput.removeAttribute('aria-invalid');
    if (consentError) consentError.textContent = '';
  });

  const lookTrack = select('[data-look-track]');
  const lookCount = select('[data-look-count]');
  if (lookTrack && lookCount) {
    let queued = false;
    const updateLookCount = () => {
      const cards = selectAll('[data-look-card]', lookTrack);
      if (!cards.length) return;
      const centers = cards.map((card) => Math.abs(card.offsetLeft + card.offsetWidth / 2 - lookTrack.scrollLeft - lookTrack.clientWidth / 2));
      const activeIndex = centers.indexOf(Math.min(...centers));
      lookCount.textContent = `0${activeIndex + 1} / 0${cards.length}`;
      queued = false;
    };
    lookTrack.addEventListener('scroll', () => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(updateLookCount);
    }, { passive: true });
  }

  if (!reduceMotion && window.matchMedia('(pointer: fine)').matches) {
    selectAll('[data-look-card]').forEach((card) => {
      card.addEventListener('pointermove', (event) => {
        const bounds = card.getBoundingClientRect();
        const x = ((event.clientX - bounds.left) / bounds.width - .5) * -10;
        const y = ((event.clientY - bounds.top) / bounds.height - .5) * -10;
        card.style.setProperty('--move-x', `${x}px`);
        card.style.setProperty('--move-y', `${y}px`);
      });
      card.addEventListener('pointerleave', () => {
        card.style.removeProperty('--move-x');
        card.style.removeProperty('--move-y');
      });
    });
  }

  const revealItems = selectAll('[data-reveal]');
  if (!reduceMotion && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: .1 });
    revealItems.forEach((item) => observer.observe(item));
  } else {
    revealItems.forEach((item) => item.classList.add('is-visible'));
  }

  selectAll('[data-product-thumb]').forEach((thumb) => {
    thumb.addEventListener('click', () => {
      const gallery = thumb.closest('[data-product-gallery]');
      selectAll('[data-product-media]', gallery).forEach((media) => media.classList.toggle('is-hidden', media.dataset.productMedia !== thumb.dataset.productThumb));
      selectAll('[data-product-thumb]', gallery).forEach((item) => item.classList.toggle('is-active', item === thumb));
    });
  });
})();
